import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Plus,
  Clock,
  MapPin,
  User,
  MoreVertical,
  Calendar,
  SlidersHorizontal as Filters,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Users,
  Clock9,
  Eye,
  CheckCircle,
  Pencil,
  Trash2
} from 'lucide-react';
import Sidebar from '../../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../../components/Dashboard/Header/Header';
import SummaryCard from '../../../components/Dashboard/SummaryCard/SummaryCard';
import Breadcrumbs from '../../../components/Common/Breadcrumbs/Breadcrumbs';
import Footer from '../../../components/Common/Footer/Footer';
import CustomSelect from '../../../components/Common/Select/CustomSelect';
import styles from './Overtime.module.css';
import OTAttendanceDrawer from './OTAttendanceDrawer';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

const MySwal = withReactContent(Swal);

const OTAttendance = () => {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [openMenuIndex, setOpenMenuIndex] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

  const [filters, setFilters] = useState({
    status: null,
    site: null,
    date: ''
  });

  const summaryData = [
    {
      icon: <Users size={20} />,
      label: 'Staff on OT',
      value: '12',
      trend: '+2',
      trendType: 'up',
      iconBg: '#3b82f6'
    },
    {
      icon: <Clock size={20} />,
      label: 'Total OT Hours',
      value: '42.5',
      trend: '+8.2',
      trendType: 'up',
      iconBg: '#6366f1'
    },
    {
      icon: <AlertCircle size={20} />,
      label: 'Pending Approval',
      value: '05',
      trend: '-1',
      trendType: 'up',
      iconBg: '#f59e0b'
    },
    {
      icon: <CheckCircle2 size={20} />,
      label: 'Approved Today',
      value: '07',
      trend: '+3',
      trendType: 'up',
      iconBg: '#10b981'
    }
  ];

  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' }
  ];

  const siteOptions = [
    { value: 1, label: 'Downtown Plaza' },
    { value: 2, label: 'Skyline Residency' }
  ];

  // Mock Data
  const [attendance, setAttendance] = useState([
    {
      id: 1,
      employee: 'Michael Chen',
      employee_id: 101,
      project_site: 'Downtown Plaza',
      date: '2024-04-30',
      ot_punch_in: '18:30:00',
      ot_punch_out: '21:30:00',
      ot_hours: '3.0',
      status: 'approved'
    },
    {
      id: 2,
      employee: 'Sarah Johnson',
      employee_id: 105,
      project_site: 'Skyline Residency',
      date: '2024-05-01',
      ot_punch_in: '19:00:00',
      ot_punch_out: '22:45:00',
      ot_hours: '3.75',
      status: 'pending'
    }
  ]);

  const toggleActionMenu = (id, event) => {
    if (openMenuIndex === id) {
      setOpenMenuIndex(null);
    } else {
      const rect = event.currentTarget.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 5,
        left: rect.left - 130
      });
      setOpenMenuIndex(id);
    }
  };

  const handleApprove = (id) => {
    setOpenMenuIndex(null);
    MySwal.fire({
      title: 'Approve OT Hours?',
      text: "This will finalize the OT hours for payment processing.",
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, Approve',
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#64748b',
      customClass: {
        confirmButton: styles['swal-confirm-btn'],
        cancelButton: styles['swal-cancel-btn']
      }
    }).then((result) => {
      if (result.isConfirmed) {
        setAttendance(attendance.map(item =>
          item.id === id ? { ...item, status: 'approved' } : item
        ));
        MySwal.fire('Approved!', 'OT hours have been verified.', 'success');
      }
    });
  };

  const handleDelete = (record) => {
    setOpenMenuIndex(null);
    MySwal.fire({
      title: 'Are you sure?',
      text: `Do you want to delete OT log for "${record.employee}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, delete it!',
      customClass: {
        confirmButton: styles['swal-confirm-btn'],
        cancelButton: styles['swal-cancel-btn']
      }
    }).then((result) => {
      if (result.isConfirmed) {
        setAttendance(prev => prev.filter(a => a.id !== record.id));
        MySwal.fire('Deleted!', 'The record has been removed.', 'success');
      }
    });
  };

  const handleAddClick = () => {
    setEditingRecord(null);
    setIsDrawerOpen(true);
  };

  const handleEditClick = (record) => {
    setOpenMenuIndex(null);
    setEditingRecord(record);
    setIsDrawerOpen(true);
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'approved': return styles['status-approved'];
      case 'rejected': return styles['status-rejected'];
      default: return styles['status-pending'];
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return <CheckCircle2 size={12} />;
      case 'rejected': return <XCircle size={12} />;
      default: return <AlertCircle size={12} />;
    }
  };

  const hasActiveFilters = filters.status || filters.site || filters.date;

  return (
    <div className={styles['overtime-container']} onClick={() => setOpenMenuIndex(null)}>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={() => setIsSidebarOpen(true)} />

        <main className={styles['main-content']}>
          <Breadcrumbs customItems={[
            { label: 'HR', path: '#' },
            { label: 'OT Attendance', path: '/employees/ot-attendance' }
          ]} />

          <div className={styles['page-header']}>
            <div className={styles['header-left']}>
              <h1>OT Attendance Logs</h1>
              <p>Verify actual overtime hours and approve shift completions.</p>
            </div>
            <div className={styles['header-actions']}>
              <button className={styles['add-btn']} onClick={handleAddClick}>
                <Plus size={18} />
                <span>Log OT Hours</span>
              </button>
            </div>
          </div>

          <div className={styles['summary-grid']}>
            {summaryData.map((data, index) => (
              <SummaryCard key={index} {...data} />
            ))}
          </div>

          <div className={styles['table-controls']}>
            <div className={styles['search-wrapper']}>
              <Search size={18} className={styles['search-icon']} />
              <input
                type="text"
                placeholder="Search logs..."
                className={styles['search-input']}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            <div className={styles['filter-actions']}>
              <button
                className={`${styles['filter-toggle']} ${hasActiveFilters ? styles['active'] : ''}`}
                onClick={(e) => { e.stopPropagation(); setShowFilters(!showFilters); }}
              >
                <Filters size={18} />
                <span>Filters</span>
                {hasActiveFilters && <span className={styles['filter-badge']} />}
              </button>
            </div>
          </div>

          {showFilters && (
            <div className={styles['filters-panel']} onClick={(e) => e.stopPropagation()}>
              <div className={styles['filters-grid']}>
                <div className={styles['filter-group']}>
                  <label>Status</label>
                  <CustomSelect
                    options={statusOptions}
                    value={statusOptions.find(o => o.value === filters.status)}
                    onChange={(opt) => setFilters({ ...filters, status: opt ? opt.value : null })}
                    placeholder="All Status"
                    isClearable
                  />
                </div>
                <div className={styles['filter-group']}>
                  <label>Project Site</label>
                  <CustomSelect
                    options={siteOptions}
                    value={siteOptions.find(o => o.value === filters.site)}
                    onChange={(opt) => setFilters({ ...filters, site: opt ? opt.value : null })}
                    placeholder="All Sites"
                    isClearable
                  />
                </div>
                <div className={styles['filter-group']}>
                  <label>Date</label>
                  <input
                    type="date"
                    className={styles['date-input']}
                    value={filters.date}
                    onChange={(e) => setFilters({ ...filters, date: e.target.value })}
                  />
                </div>
              </div>
              <div className={styles['filters-footer']}>
                <button
                  className={styles['reset-btn']}
                  onClick={() => setFilters({ status: null, site: null, date: '' })}
                >
                  Reset
                </button>
                <button
                  className={styles['close-filters-btn']}
                  onClick={() => setShowFilters(false)}
                >
                  Close
                </button>
              </div>
            </div>
          )}

          <div className={styles['data-view-container']}>
            <div className={styles['table-responsive']}>
              <table className={styles['data-table']}>
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Site & Date</th>
                    <th>Punch In/Out</th>
                    <th>OT Hours</th>
                    <th>Verification</th>
                    <th className={styles['actions-cell']}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {attendance.map((record) => (
                    <tr key={record.id}>
                      <td>
                        <div className={styles['user-identity']}>
                          <div className={styles['avatar-small']} style={{ background: '#fef3c7', color: '#d97706' }}>
                            {record.employee.charAt(0)}
                          </div>
                          <div className={styles['user-info']}>
                            <span
                              className={`${styles['user-name']} ${styles['clickable']}`}
                              onClick={() => navigate(`/employees/ot-attendance/view/${record.id}`)}
                            >
                              {record.employee}
                            </span>
                            <span className={styles['user-site']}>{record.employee_id}</span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className={styles['user-info']}>
                          <span className={styles['user-site']} style={{ fontSize: '13px', fontWeight: 600 }}>{record.project_site}</span>
                          <span className={styles['user-site']}><Calendar size={12} /> {record.date}</span>
                        </div>
                      </td>
                      <td>
                        <div className={styles['user-info']}>
                          <span style={{ fontSize: '13px' }}><strong>In:</strong> {record.ot_punch_in}</span>
                          <span style={{ fontSize: '13px' }}><strong>Out:</strong> {record.ot_punch_out}</span>
                        </div>
                      </td>
                      <td>
                        <div className={styles['user-identity']}>
                          <Clock size={16} color="#6366f1" />
                          <span style={{ fontWeight: 800, color: '#4f46e5' }}>{record.ot_hours}h</span>
                        </div>
                      </td>
                      <td>
                        <span className={`${styles['status-badge']} ${getStatusBadgeClass(record.status)}`}>
                          {getStatusIcon(record.status)}
                          {record.status}
                        </span>
                      </td>
                      <td className={styles['actions-cell']}>
                        <div className={styles['action-wrapper']}>
                          <button
                            className={`${styles['action-btn']} ${openMenuIndex === record.id ? styles['active'] : ''}`}
                            onClick={(e) => { e.stopPropagation(); toggleActionMenu(record.id, e); }}
                          >
                            <MoreVertical size={18} />
                          </button>

                          {openMenuIndex === record.id && (
                            <div
                              className={styles['action-dropdown']}
                              style={{ top: menuPosition.top, left: menuPosition.left }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {record.status === 'pending' && (
                                <button className={styles['dropdown-item']} onClick={() => handleApprove(record.id)}>
                                  <CheckCircle size={16} color="#10b981" /> Approve
                                </button>
                              )}
                              <button className={styles['dropdown-item']} onClick={() => navigate(`/employees/ot-attendance/view/${record.id}`)}>
                                <Eye size={16} /> View Details
                              </button>
                              <button className={styles['dropdown-item']} onClick={() => handleEditClick(record)}>
                                <Pencil size={16} /> Edit
                              </button>
                              <div className={styles['dropdown-divider']}></div>
                              <button
                                className={`${styles['dropdown-item']} ${styles['deselect']}`}
                                onClick={() => handleDelete(record)}
                              >
                                <Trash2 size={16} /> Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <Footer />
        </main>
      </div>

      <OTAttendanceDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        record={editingRecord}
        onSave={() => setIsDrawerOpen(false)}
      />
    </div>
  );
};

export default OTAttendance;
